import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const cardVariants = cva(
	// Base styles
	"rounded-lg border bg-card text-foreground",
	{
		variants: {
			variant: {
				// Border, fill, radius and shadow each say "separate object".
				// Spending all four on every block spends them on nothing, which
				// is why no section of a dense page currently looks more
				// important than any other. So: `default` is a bordered surface
				// with NO shadow, `elevated` keeps the shadow and is for things
				// that genuinely float (drawer, popover, toast), and `ground` is
				// a section that is not an object at all, just a heading and
				// some space.
				default: "border-border shadow-none",
				elevated: "border-border shadow-card-hover",
				outlined: "border-border shadow-none",
				ground: "border-0 shadow-none bg-transparent",
				ghost: "border-0 shadow-none bg-transparent"
			},
			padding: {
				none: "p-0",
				sm: "p-2.5",
				md: "p-4",
				lg: "p-6"
			}
		},
		defaultVariants: {
			variant: "default",
			padding: "md"
		}
	}
);

const Card = forwardRef(({
	className,
	variant,
	padding,
	...props
}, ref) => (
	<div
		ref={ref}
		className={cn(cardVariants({ variant, padding, className }))}
		{...props}
	/>
));

Card.displayName = "Card";

const CardHeader = forwardRef(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("flex flex-col space-y-1.5 p-6", className)}
		{...props}
	/>
));

CardHeader.displayName = "CardHeader";

const CardTitle = forwardRef(({ className, children, ...props }, ref) => (
	<h3
		ref={ref}
		className={cn("text-xl font-semibold leading-none tracking-tight", className)}
		{...props}
	>
		{children}
	</h3>
));

CardTitle.displayName = "CardTitle";

const CardDescription = forwardRef(({ className, ...props }, ref) => (
	<p
		ref={ref}
		className={cn("text-sm text-muted-foreground", className)}
		{...props}
	/>
));

CardDescription.displayName = "CardDescription";

const CardContent = forwardRef(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("p-6 pt-0", className)}
		{...props}
	/>
));

CardContent.displayName = "CardContent";

const CardFooter = forwardRef(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("flex items-center p-6 pt-0", className)}
		{...props}
	/>
));

CardFooter.displayName = "CardFooter";

export {
	Card,
	CardHeader,
	CardFooter,
	CardTitle,
	CardDescription,
	CardContent,
	cardVariants
}; 